import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const downloadServerCSV = async (path, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [salesRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/reports/sales`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/inventory`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSalesReport(salesRes.data);
      setInventoryReport(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (data, filename) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj => Object.values(obj).join(','));
    return [headers, ...rows].join('\n');
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading reports...</div>;
  }

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-600 mt-1">View and export business reports</p>
      </div>

      <Tabs defaultValue="sales" className="space-y-6">
        <TabsList>
          <TabsTrigger value="sales" data-testid="sales-report-tab">
            <FileText className="mr-2" size={16} />
            Sales Report
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="inventory-report-tab">
            <FileText className="mr-2" size={16} />
            Inventory Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales" data-testid="sales-report-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Sales Overview</CardTitle>
              <Button
                data-testid="download-sales-button"
                variant="outline"
                size="sm"
                onClick={() => downloadServerCSV('/reports/sales/export', 'sales_report.csv')}
              >
                <Download className="mr-2" size={16} />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Total Orders</p>
                  <p className="text-3xl font-bold text-blue-600">{salesReport?.total_orders || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Total Revenue</p>
                  <p className="text-3xl font-bold text-green-600">Tsh {(salesReport?.total_revenue || 0).toFixed(2)}</p>
                </div>
              </div>

              {salesReport?.recent_orders && salesReport.recent_orders.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Recent Orders</h3>
                  <div className="bg-white rounded-lg border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Order Number</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {salesReport.recent_orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.order_number}</TableCell>
                            <TableCell>{order.customer_name || (order.customer_id ? `${order.customer_id.substring(0, 8)}…` : '-')}</TableCell>
                            <TableCell>Tsh {(order.total_amount || 0).toLocaleString()}</TableCell>
                            <TableCell className="capitalize">{order.status}</TableCell>
                            <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory" data-testid="inventory-report-content">
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Raw Materials</CardTitle>
                <Button
                  data-testid="download-raw-materials-button"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadServerCSV('/reports/inventory/export', 'inventory_report.csv')}
                >
                  <Download className="mr-2" size={16} />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-slate-600">Total Raw Material Value</p>
                  <p className="text-3xl font-bold text-blue-600">Tsh {(inventoryReport?.total_raw_material_value || 0).toFixed(2)}</p>
                </div>

                {inventoryReport?.raw_materials && inventoryReport.raw_materials.length > 0 && (
                  <div className="bg-white rounded-lg border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Material Name</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Total Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventoryReport.raw_materials.map((material) => (
                          <TableRow key={material.id}>
                            <TableCell>{material.material_name}</TableCell>
                            <TableCell>{material.material_code}</TableCell>
                            <TableCell>{material.quantity}</TableCell>
                            <TableCell>{material.unit}</TableCell>
                            <TableCell>Tsh {(material.unit_price || 0).toFixed(2)}</TableCell>
                            <TableCell className="font-semibold">Tsh {((material.quantity || 0) * (material.unit_price || 0)).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Chalk Cartons</CardTitle>
                <Button
                  data-testid="download-cartons-button"
                  variant="outline"
                  size="sm"
                  onClick={() => inventoryReport?.chalk_cartons && downloadCSV(inventoryReport.chalk_cartons, 'chalk_cartons_report')}
                >
                  <Download className="mr-2" size={16} />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-slate-600">Total Cartons</p>
                  <p className="text-3xl font-bold text-green-600">{inventoryReport?.total_cartons || 0}</p>
                </div>

                {inventoryReport?.chalk_cartons && inventoryReport.chalk_cartons.length > 0 && (
                  <div className="bg-white rounded-lg border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Batch Number</TableHead>
                          <TableHead>Product Name</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Cartons/Batch</TableHead>
                          <TableHead>Quality Status</TableHead>
                          <TableHead>Production Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventoryReport.chalk_cartons.map((carton) => (
                          <TableRow key={carton.id}>
                            <TableCell>{carton.batch_number}</TableCell>
                            <TableCell>{carton.product_name}</TableCell>
                            <TableCell>{carton.quantity}</TableCell>
                            <TableCell>{carton.cartons_per_batch}</TableCell>
                            <TableCell className="capitalize">{carton.quality_status}</TableCell>
                            <TableCell>{new Date(carton.production_date).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
